import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { winesApi } from '../api/wines';
import { WineFormDialog } from './WineFormDialog';

function label(w: { wineName: string | null; vintage?: number | null; producer?: string | null }) {
  const head = `${w.vintage ? `${w.vintage} ` : ''}${w.wineName ?? 'Unnamed'}`;
  return w.producer ? `${head} — ${w.producer}` : head;
}

/**
 * Link a tasting slot to a canonical Cellar wine: search the catalog, pick a
 * match, or add a brand-new wine on the spot. Selecting one backfills the
 * slot's identity (producer/grape/region/name/vintage) server-side.
 */
export function CellarWinePicker({
  linkedLabel,
  onLink,
  onUnlink,
}: {
  linkedLabel: string | null;
  onLink: (catalogId: string) => void;
  onUnlink: () => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();

  const { data } = useQuery({
    queryKey: ['wines', { q, picker: true }],
    queryFn: () => winesApi.search({ q: q || undefined }),
    enabled: open,
    staleTime: 15_000,
  });
  const results = data?.items ?? [];

  if (linkedLabel) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-gold-600/40 bg-ink-950 px-3 py-1.5 text-xs">
        <span className="text-gold-200">🍷 {linkedLabel}</span>
        <button type="button" onClick={onUnlink} className="text-cream-300/50 hover:text-wine-300">
          unlink
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        className="field"
        placeholder="🔎 Link a wine from the Cellar…"
        value={q}
        autoComplete="off"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 150);
        }}
      />
      {open && (
        <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-md border border-ink-800 bg-ink-950 py-1 shadow-xl">
          {results.map((w) => (
            <li key={w.id}>
              <button
                type="button"
                className="block w-full truncate px-3 py-1.5 text-left text-sm text-cream-200 hover:bg-ink-800 hover:text-gold-200"
                onMouseDown={(e) => {
                  e.preventDefault();
                  clearTimeout(blurTimer.current);
                  onLink(w.id);
                  setOpen(false);
                  setQ('');
                }}
              >
                {label(w)}
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-sm text-gold-300 hover:bg-ink-800"
              onMouseDown={(e) => {
                e.preventDefault();
                clearTimeout(blurTimer.current);
                setAdding(true);
                setOpen(false);
              }}
            >
              + Add a new wine to the Cellar
            </button>
          </li>
        </ul>
      )}
      {adding && (
        <WineFormDialog
          editing={null}
          onClose={() => setAdding(false)}
          onSaved={(id) => {
            setAdding(false);
            onLink(id);
          }}
        />
      )}
    </div>
  );
}
